import { investmentProductRow, calculateInvestmentProduct, type InvestmentProduct } from './investmentProduct';
import type { JSONContent } from '@tiptap/core';
import { generateHTML, generateJSON } from '@tiptap/html';
import { BlockType } from '@/types';
import type {
  Block,
  InvestmentBlockContent,
  InvestmentRateCard,
  InvestmentTable,
  ContentPageBlockContent,
  PlanVanAanpakBlockContent,
} from '@/types';
import { formatCurrency, formatProductPrice, isCurrencyAmount, parseEuroAmount } from '@/lib/format';
import {
  investmentCellPlainText,
  investmentCellShouldStoreHtml,
  investmentDescriptionToCellParagraphs,
  investmentFirstCellRichHtml,
  investmentTitleCellContent,
} from '@/editor/investmentTableCellContent';
import {
  buildInvestmentHeaderRowJson,
  getInvestmentTableLabels,
  normalizeInvestmentLabelLanguage,
} from '@/editor/investmentTableLabels';
import { sectionContentExtensions } from '@/editor/sectionContentExtensions';
import {
  applyInvestmentColumnWidths,
  readInvestmentColumnWidths,
} from '@/editor/investmentColumnWidths';

/** Maximaal aantal tableCell-kolommen in een datarij (zonder kopregel); vangt inconsistente eerste rij op. */
function maxDataRowTableCellCountFromJson(table: JSONContent): number | null {
  const tableRows = table.content;
  if (!Array.isArray(tableRows)) return null;
  let max = 0;
  let any = false;
  for (const tr of tableRows) {
    if (tr.type !== 'tableRow') continue;
    const cells = tr.content || [];
    if (cells[0]?.type === 'tableHeader') continue;
    let n = 0;
    for (const c of cells) {
      if (c.type === 'tableCell') n++;
    }
    any = true;
    if (n > max) max = n;
  }
  return any ? max : null;
}

function peekInvestmentTotalPricingType(
  children: JSONContent[],
  tableIndex: number,
): InvestmentTable['pricingType'] | undefined {
  for (let j = tableIndex + 1; j < children.length; j++) {
    const nxt = children[j];
    if (nxt.type === 'investmentTotalNode') {
      const pt = (nxt.attrs as Record<string, unknown>)?.pricingType;
      return pt === 'hourly' || pt === 'fixed' || pt === 'recurring' ? pt : undefined;
    }
    if (nxt.type === 'investmentHeaderNode') {
      const h = nxt.attrs as Record<string, unknown>;
      if (!h.isMainHeader) break;
    }
  }
  return undefined;
}

/** Prioriteit: fysieke kolomstructuur > totalNode-attr > phase-header-attr. */
function resolveInvestmentTablePricingTypeFromSignals(
  headerPt: InvestmentTable['pricingType'],
  totalPt: InvestmentTable['pricingType'] | undefined,
  cellCount: number | null,
): InvestmentTable['pricingType'] {
  if (cellCount != null) {
    if (cellCount >= 4) return 'hourly';
    if (cellCount === 2) return 'recurring';
    if (cellCount === 1) return 'fixed';
    if (cellCount === 3) return 'hourly';
  }
  if (totalPt === 'hourly' || totalPt === 'fixed' || totalPt === 'recurring') return totalPt;
  if (headerPt === 'hourly' || headerPt === 'fixed' || headerPt === 'recurring') return headerPt;
  return 'fixed';
}

/**
 * Maps BlockType to Tiptap node name.
 * CONTENT_PAGE and PLAN_VAN_AANPAK use section nodes (non-atom, splittable).
 * INVESTMENT uses a group node with header + table children.
 */
const BLOCK_TYPE_TO_NODE: Record<BlockType, string> = {
  [BlockType.TEXT]: 'textBlock',
  [BlockType.IMAGE]: 'imageBlock',
  [BlockType.VIDEO]: 'videoBlock',
  [BlockType.TABLE]: 'tableBlock',
  [BlockType.PRODUCT]: 'productBlock',
  [BlockType.PRICING]: 'pricingBlock',
  [BlockType.DIVIDER]: 'dividerBlock',
  [BlockType.SPACER]: 'spacerBlock',
  [BlockType.SIGNATURE]: 'signatureBlock',
  [BlockType.HEADER]: 'headerBlock',
  [BlockType.COVER]: 'coverBlock',
  [BlockType.TEAM]: 'teamBlock',
  [BlockType.CONTENT_PAGE]: 'contentPageSection',
  [BlockType.INVESTMENT]: 'investmentGroupNode',
  [BlockType.PLAN_VAN_AANPAK]: 'planVanAanpakSection',
  [BlockType.FOOTER]: 'footerBlock',
  [BlockType.SIGNATURE_PAGE]: 'signaturePageBlock',
};

const NODE_TO_BLOCK_TYPE: Record<string, BlockType> = {};
for (const [blockType, nodeName] of Object.entries(BLOCK_TYPE_TO_NODE)) {
  NODE_TO_BLOCK_TYPE[nodeName] = blockType as BlockType;
}

function emptySectionParagraph(): JSONContent[] {
  return [{ type: 'paragraph', content: [{ type: 'text', text: ' ' }] }];
}

/**
 * HTML ↔ JSON voor contentpagina / Plan van Aanpak via dezelfde TipTap-schema als de editor
 * (marks, tabellen, variabelen, afbeeldingen).
 */
function htmlToTiptapContent(html: string): JSONContent[] {
  if (!html || html.trim() === '') {
    return emptySectionParagraph();
  }
  try {
    const trimmed = html.trim();
    const wrapped = trimmed.startsWith('<') ? trimmed : `<p>${trimmed}</p>`;
    const json = generateJSON(wrapped, sectionContentExtensions) as JSONContent;
    const nodes = json?.content;
    if (!nodes || nodes.length === 0) {
      return emptySectionParagraph();
    }
    return nodes;
  } catch (e) {
    console.warn('[blockConversion] generateJSON failed, using empty paragraph', e);
    return emptySectionParagraph();
  }
}

function tiptapContentToHtml(content: JSONContent[]): string {
  if (!content || content.length === 0) return '';
  try {
    const doc = { type: 'doc', content } as JSONContent;
    return generateHTML(doc, sectionContentExtensions);
  } catch (e) {
    console.warn('[blockConversion] generateHTML failed', e);
    return '';
  }
}

/**
 * Converts blocks array to Tiptap doc JSON.
 * Section nodes (CONTENT_PAGE, PLAN_VAN_AANPAK) get their HTML content converted to native Tiptap nodes.
 * INVESTMENT blocks are split into investmentGroupNode > investmentHeaderNode + native table + investmentTotalNode per phase.
 */
export function blocksToTiptapDoc(blocks: Block[]): JSONContent {
  const content: JSONContent[] = [];

  const sorted = [...blocks].sort((a, b) => a.order - b.order);

  for (const block of sorted) {
    const nodeName = BLOCK_TYPE_TO_NODE[block.type];
    if (!nodeName) continue;

    if (block.type === BlockType.CONTENT_PAGE) {
      const c = block.content as ContentPageBlockContent;
      const tiptapContent = htmlToTiptapContent(c.content || '');
      content.push({
        type: 'contentPageSection',
        attrs: {
          id: block.id,
          quoteId: block.quoteId,
          order: block.order,
          sectionStyle: c.style || 'default',
          theme: c.theme ?? null,
          clientLogoUrl: c.clientLogoUrl || null,
          agencyLogoUrl: c.agencyLogoUrl || '/ubo/logo.svg',
          styles: block.styles ?? {},
          config: block.config ?? {},
        },
        content: tiptapContent,
      });
    } else if (block.type === BlockType.PLAN_VAN_AANPAK) {
      const c = block.content as PlanVanAanpakBlockContent;
      const bodyNodes = htmlToTiptapContent(c.content || '');
      content.push({
        type: 'planVanAanpakSection',
        attrs: {
          id: block.id,
          quoteId: block.quoteId,
          order: block.order,
          sectionStyle: 'default',
          theme: c.theme ?? null,
          clientLogoUrl: c.clientLogoUrl || null,
          agencyLogoUrl: c.agencyLogoUrl || '/ubo/logo.svg',
          title: c.title || 'Plan van Aanpak',
          showCompanyData: c.showCompanyData ?? false,
          companyName: c.companyName || '',
          companyAddress: c.companyAddress || '',
          companyCity: c.companyCity || '',
          companyZip: c.companyZip || '',
          styles: block.styles ?? {},
          config: block.config ?? {},
        },
        content: bodyNodes,
      });
    } else if (block.type === BlockType.INVESTMENT) {
      const c = block.content as InvestmentBlockContent;
      const tables = c.tables || [];
      const invCurrency = c.currency ?? 'EUR';
      const invLang = normalizeInvestmentLabelLanguage(c.labelLanguage);
      const L = getInvestmentTableLabels(invLang);

      const groupChildren: JSONContent[] = [];

      // Main header (title for the whole investment block)
      groupChildren.push({
        type: 'investmentHeaderNode',
        attrs: {
          mainTitle: c.mainTitle || 'Investeringsoverzicht',
          isMainHeader: true,
          phaseTitle: '',
          pricingType: 'fixed',
          tableId: null,
          clientLogoUrl: c.clientLogoUrl || null,
          agencyLogoUrl: c.agencyLogoUrl || '/ubo/logo.svg',
        },
      });

      for (const table of tables) {
        const pt = table.pricingType;

        groupChildren.push({
          type: 'investmentHeaderNode',
          attrs: {
            mainTitle: '',
            isMainHeader: false,
            phaseTitle: table.title,
            phaseSubtitle: table.subtitle ?? '',
            pricingType: pt,
            tableId: table.id,
            clientLogoUrl: null,
            agencyLogoUrl: null,
            // c.showHourColumns is legacy (stond ooit op blokniveau) en dient nu
            // alleen nog als startwaarde voor tabellen van vóór die verhuizing.
            showHourColumns: (table.showHourColumns ?? c.showHourColumns) !== false,
            showRowPrices: table.showRowPrices !== false,
          },
        });

        const headerRow = buildInvestmentHeaderRowJson(pt, L);
        const headerCells = (headerRow.content as JSONContent[]) || [];

        const dataRows: JSONContent[] = (table.rows || []).map((row) => {
          if (row.productPrice) {
            const amount = formatProductPrice({ minCents: row.productPrice.totalCents, maxCents: row.productPrice.maxTotalCents, priceText: row.productPrice.priceText }, invCurrency);
            const texts = pt === 'hourly' ? [row.title, '', '', amount] : pt === 'fixed' ? [row.title] : [row.title, amount];
            return {
              type: 'tableRow', attrs: { productPrice: row.productPrice },
              content: applyInvestmentColumnWidths(texts.map((text) => ({
                type: 'tableCell', content: investmentDescriptionToCellParagraphs(text),
              })), pt, table.columnWidths),
            };
          }
          if (row.investmentProduct) {
            const json = investmentProductRow(row.investmentProduct, pt, invCurrency);
            json.content = applyInvestmentColumnWidths(json.content ?? [], pt, table.columnWidths);
            return json;
          }
          let cells: JSONContent[];
          if (pt === 'hourly') {
            const price = row.price ?? 0;
            const hours = row.hours ?? 0;
            const sub = price * hours;
            const amountText = row.useText ? row.text ?? '' : formatCurrency(price, invCurrency);
            const subtotalText =
              row.subtotalText?.trim() ||
              (row.useText ? amountText : formatCurrency(sub, invCurrency));
            cells = [
              { type: 'tableCell', content: investmentTitleCellContent(row) },
              { type: 'tableCell', content: [{ type: 'paragraph', ...(amountText ? { content: [{ type: 'text', text: amountText }] } : {}) }] },
              { type: 'tableCell', content: [{ type: 'paragraph', ...(!row.useText && row.hours != null ? { content: [{ type: 'text', text: String(hours) }] } : {}) }] },
              { type: 'tableCell', content: [{ type: 'paragraph', ...(subtotalText ? { content: [{ type: 'text', text: subtotalText }] } : {}) }] },
            ];
          } else if (pt === 'fixed') {
            cells = [
              { type: 'tableCell', content: investmentTitleCellContent(row) },
            ];
          } else {
            const amountText = row.useText ? row.text ?? '' : formatCurrency(row.subtotal ?? 0, invCurrency);
            cells = [
              { type: 'tableCell', content: investmentTitleCellContent(row) },
              { type: 'tableCell', content: [{ type: 'paragraph', ...(amountText ? { content: [{ type: 'text', text: amountText }] } : {}) }] },
            ];
          }
          // Productkoppeling meegeven als rij-attributen; de cellen blijven tekst.
          const linkAttrs: Record<string, unknown> = {};
          if (row.rateKey) linkAttrs.rateKey = row.rateKey;
          if (typeof row.showHours === 'boolean') linkAttrs.showHours = row.showHours;
          if (row.productId) linkAttrs.productId = row.productId;
          if (row.productVersionId) linkAttrs.productVersionId = row.productVersionId;
          if (row.variantId) linkAttrs.variantId = row.variantId;
          if (row.quantity != null) linkAttrs.quantity = row.quantity;
          if (row.discountPct != null) linkAttrs.discountPct = row.discountPct;
          if (row.hasOverrides) linkAttrs.hasOverrides = true;

          return {
            type: 'tableRow',
            ...(Object.keys(linkAttrs).length ? { attrs: linkAttrs } : {}),
            content: applyInvestmentColumnWidths(cells, pt, table.columnWidths),
          };
        });

        headerRow.content = applyInvestmentColumnWidths(headerCells, pt, table.columnWidths);

        if (dataRows.length === 0) {
          const colCount = pt === 'hourly' ? 4 : pt === 'fixed' ? 1 : 2;
          const emptyCells = Array.from({ length: colCount }, () => ({
            type: 'tableCell' as const,
            content: [{ type: 'paragraph' as const }],
          }));
          dataRows.push({
            type: 'tableRow',
            content: applyInvestmentColumnWidths(emptyCells, pt, table.columnWidths),
          });
        }

        groupChildren.push({
          type: 'table',
          content: [headerRow, ...dataRows],
        });

        groupChildren.push({
          type: 'investmentTotalNode',
          attrs: {
            tableId: table.id,
            pricingType: pt,
            label: L.totalRow,
            fixedTotal: pt === 'fixed' ? table.fixedTotal ?? 0 : 0,
            fixedTotalText: pt === 'fixed' ? table.fixedTotalText ?? null : null,
            showTotalRow: table.showTotalRow !== false,
          },
        });
      }

      if (tables.length === 0) {
        const defaultId = `default-${Date.now()}`;
        groupChildren.push({
          type: 'investmentHeaderNode',
          attrs: {
            mainTitle: '',
            isMainHeader: false,
            phaseTitle: 'Nieuw investeringsoverzicht',
            phaseSubtitle: '',
            pricingType: 'fixed',
            tableId: defaultId,
            clientLogoUrl: null,
            agencyLogoUrl: null,
          },
        });
        groupChildren.push({
          type: 'table',
          content: [
            { type: 'tableRow', content: [
              { type: 'tableHeader', content: [{ type: 'paragraph', content: [{ type: 'text', text: L.description }] }] },
            ]},
            { type: 'tableRow', content: [
              { type: 'tableCell', content: [{ type: 'paragraph' }] },
            ]},
          ],
        });
        groupChildren.push({
          type: 'investmentTotalNode',
          attrs: { tableId: defaultId, pricingType: 'fixed', label: L.totalRow, fixedTotal: 0, showTotalRow: true },
        });
      }

      content.push({
        type: 'investmentGroupNode',
        attrs: {
          id: block.id,
          quoteId: block.quoteId,
          order: block.order,
          clientLogoUrl: c.clientLogoUrl || null,
          agencyLogoUrl: c.agencyLogoUrl || '/ubo/logo.svg',
          currency: invCurrency,
          labelLanguage: invLang,
          theme: c.theme ?? null,
          rateCard: c.rateCard ?? null,
          styles: block.styles ?? {},
          config: block.config ?? {},
        },
        content: groupChildren,
      });
    } else {
      content.push({
        type: nodeName,
        attrs: {
          id: block.id,
          quoteId: block.quoteId,
          order: block.order,
          content: block.content,
          styles: block.styles ?? {},
          config: block.config ?? {},
        },
      });
    }
  }

  return {
    type: 'doc',
    content: content.length > 0 ? content : [{ type: 'paragraph', content: [] }],
  };
}

/**
 * Converts Tiptap doc JSON back to blocks array.
 * Section nodes have their native Tiptap content converted back to HTML.
 * Investment group nodes are merged back into a single INVESTMENT block.
 *
 * @param previousBlocks — vorige blocks om `createdAt` en investment-rij-id's stabiel te houden.
 */
export function tiptapDocToBlocks(doc: JSONContent, quoteId: string, previousBlocks?: Block[]): Block[] {
  const blocks: Block[] = [];
  const content = doc?.content;
  if (!Array.isArray(content)) return blocks;

  const now = new Date();
  const prevById = new Map<string, Block>();
  if (previousBlocks) {
    for (const b of previousBlocks) {
      prevById.set(b.id, b);
    }
  }
  const createdAtFor = (blockId: string) => prevById.get(blockId)?.createdAt ?? now;

  let order = 0;

  for (const node of content) {
    const nodeType = node.type as string;
    if (nodeType === 'paragraph' || nodeType === 'doc') continue;

    if (nodeType === 'contentPageSection') {
      const attrs = node.attrs as Record<string, unknown>;
      const blockId = (attrs.id as string) ?? `block-${Date.now()}-${order}`;
      const htmlContent = node.content ? tiptapContentToHtml(node.content) : '';
      blocks.push({
        id: blockId,
        quoteId,
        type: BlockType.CONTENT_PAGE,
        order,
        content: {
          content: htmlContent,
          style: attrs.sectionStyle || 'default',
          theme:
            attrs.theme === 'ubx' || attrs.theme === 'ubo' ? attrs.theme : undefined,
          clientLogoUrl: attrs.clientLogoUrl || undefined,
          agencyLogoUrl: attrs.agencyLogoUrl || '/ubo/logo.svg',
        } as ContentPageBlockContent,
        styles: (attrs.styles as Block['styles']) ?? {},
        config: (attrs.config as Block['config']) ?? {},
        createdAt: createdAtFor(blockId),
        updatedAt: now,
      });
      order += 1;
    } else if (nodeType === 'planVanAanpakSection') {
      const attrs = node.attrs as Record<string, unknown>;
      const blockId = (attrs.id as string) ?? `block-${Date.now()}-${order}`;
      const planContent = [...(node.content ?? [])];
      const firstNode = planContent[0];
      const title = String(attrs.title || 'Plan van Aanpak');
      if (
        firstNode?.type === 'heading' &&
        firstNode.attrs?.level === 1 &&
        firstNode.content?.map((item) => item.text ?? '').join('') === title
      ) {
        planContent.shift();
      }
      const htmlContent = tiptapContentToHtml(planContent);
      blocks.push({
        id: blockId,
        quoteId,
        type: BlockType.PLAN_VAN_AANPAK,
        order,
        content: {
          title,
          content: htmlContent,
          showCompanyData: attrs.showCompanyData ?? false,
          companyName: attrs.companyName || '',
          companyAddress: attrs.companyAddress || '',
          companyCity: attrs.companyCity || '',
          companyZip: attrs.companyZip || '',
          theme:
            attrs.theme === 'ubx' || attrs.theme === 'ubo' ? attrs.theme : undefined,
          clientLogoUrl: attrs.clientLogoUrl || undefined,
          agencyLogoUrl: attrs.agencyLogoUrl || '/ubo/logo.svg',
        } as PlanVanAanpakBlockContent,
        styles: (attrs.styles as Block['styles']) ?? {},
        config: (attrs.config as Block['config']) ?? {},
        createdAt: createdAtFor(blockId),
        updatedAt: now,
      });
      order += 1;
    } else if (nodeType === 'investmentGroupNode') {
      const attrs = node.attrs as Record<string, unknown>;
      const blockId = (attrs.id as string) ?? `block-${Date.now()}-${order}`;
      const prevInv = prevById.get(blockId);
      const prevInvContent =
        prevInv?.type === BlockType.INVESTMENT ? (prevInv.content as InvestmentBlockContent) : undefined;
      const children = node.content || [];

      let mainTitle = 'Investeringsoverzicht';
      let headerClientLogo: string | undefined;
      let headerAgencyLogo = '/ubo/logo.svg';
      const tables: InvestmentTable[] = [];

      let currentPhaseTitle = '';
      let currentPhaseSubtitle: string | undefined;
      let currentPricingType: InvestmentTable['pricingType'] = 'fixed';
      let currentShowHourColumns = true;
      let currentShowRowPrices = true;
      let currentTableId = '';

      for (let ci = 0; ci < children.length; ci++) {
        const child = children[ci];
        if (child.type === 'investmentHeaderNode') {
          const hAttrs = child.attrs as Record<string, unknown>;
          if (hAttrs.isMainHeader) {
            mainTitle = (hAttrs.mainTitle as string) || mainTitle;
            headerClientLogo = hAttrs.clientLogoUrl as string | undefined;
            headerAgencyLogo = (hAttrs.agencyLogoUrl as string) || headerAgencyLogo;
          } else {
            currentPhaseTitle = (hAttrs.phaseTitle as string) || 'Investeringsoverzicht';
            const sub = (hAttrs.phaseSubtitle as string)?.trim();
            currentPhaseSubtitle = sub ? sub : undefined;
            currentPricingType = (hAttrs.pricingType as InvestmentTable['pricingType']) || 'fixed';
            currentShowHourColumns = hAttrs.showHourColumns !== false;
            currentShowRowPrices = hAttrs.showRowPrices !== false;
            currentTableId = (hAttrs.tableId as string) || `table-${Date.now()}-${tables.length}`;
          }
        } else if (child.type === 'table') {
          const rows: InvestmentTable['rows'] = [];
          const tableRows = child.content || [];
          const prevTable = prevInvContent?.tables?.find((t) => t.id === currentTableId);
          let dataRowIdx = 0;

          const headerPt = currentPricingType;
          const totalPt = peekInvestmentTotalPricingType(children, ci);
          const cellCount = maxDataRowTableCellCountFromJson(child);
          const tablePricingType = resolveInvestmentTablePricingTypeFromSignals(headerPt, totalPt, cellCount);

          for (let ri = 0; ri < tableRows.length; ri++) {
            const tr = tableRows[ri];
            if (tr.type !== 'tableRow') continue;
            const cells = tr.content || [];
            const groupedProduct = tr.attrs?.investmentProduct as InvestmentProduct | undefined;
            if (groupedProduct) {
              const computed = calculateInvestmentProduct(groupedProduct);
              rows.push({
                id: groupedProduct.id, title: groupedProduct.productName,
                investmentProduct: groupedProduct, productId: groupedProduct.productId,
                productVersionId: groupedProduct.productVersionId, variantId: groupedProduct.variantId,
                quantity: groupedProduct.quantity, discountPct: groupedProduct.discountPct,
                hasOverrides: computed.adjusted, subtotal: (computed.totalCents ?? 0) / 100,
                ...(computed.priceText !== null ? { useText: true, text: computed.priceText } : {}),
              });
              dataRowIdx += 1;
              continue;
            }
            if (cells[0]?.type === 'tableHeader') continue;

            const cellTexts = cells.map((cell: JSONContent) => investmentCellPlainText(cell));
            const stableRowId =
              prevTable?.rows[dataRowIdx]?.id ?? `row-${currentTableId || 'table'}-${Date.now()}-${dataRowIdx}`;
            const rowAttrs = (tr.attrs ?? {}) as Record<string, unknown>;
            const rowSettings = {
              ...(typeof rowAttrs.rateKey === 'string' && rowAttrs.rateKey ? { rateKey: rowAttrs.rateKey } : {}),
              ...(typeof rowAttrs.showHours === 'boolean' ? { showHours: rowAttrs.showHours } : {}),
            };
            const productLink = rowAttrs.productId
              ? {
                  productId: String(rowAttrs.productId),
                  productVersionId: String(rowAttrs.productVersionId ?? ''),
                  ...(rowAttrs.variantId ? { variantId: String(rowAttrs.variantId) } : {}),
                  ...(rowAttrs.quantity != null ? { quantity: Number(rowAttrs.quantity) } : {}),
                  ...(rowAttrs.discountPct != null
                    ? { discountPct: Number(rowAttrs.discountPct) }
                    : {}),
                  ...(rowAttrs.hasOverrides ? { hasOverrides: true } : {}),
                }
              : {};
            const firstCellJson = cells[0] as JSONContent | undefined;
            const richHtml =
              firstCellJson && investmentCellShouldStoreHtml(firstCellJson)
                ? investmentFirstCellRichHtml(firstCellJson)
                : undefined;
            dataRowIdx += 1;

            if (tablePricingType === 'hourly') {
              const priceInput = cellTexts[1]?.trim() ?? '';
              const priceUsesText = priceInput !== '' && !isCurrencyAmount(priceInput);
              const subtotalInput = cellTexts[3]?.trim() ?? '';
              const subtotalUsesText =
                subtotalInput !== '' &&
                !isCurrencyAmount(subtotalInput) &&
                (!priceUsesText || subtotalInput !== priceInput);
              const price = priceUsesText ? 0 : parseEuroAmount(priceInput);
              const hours = parseEuroAmount(cellTexts[2]);
              rows.push({
                id: stableRowId,
                title: cellTexts[0] || '',
                ...(richHtml ? { titleHtml: richHtml } : {}),
                ...productLink,
                ...rowSettings,
                price,
                hours,
                subtotal: priceUsesText || subtotalUsesText ? 0 : price * hours,
                ...(priceUsesText ? { text: priceInput, useText: true } : {}),
                ...(subtotalUsesText ? { subtotalText: subtotalInput } : {}),
              });
            } else if (tablePricingType === 'fixed') {
              if (cellTexts.length >= 2) {
                rows.push({
                  id: stableRowId,
                  title: cellTexts[0] || '',
                  ...(richHtml ? { titleHtml: richHtml } : {}),
                  ...productLink,
                  ...rowSettings,
                  subtotal: parseEuroAmount(cellTexts[1]),
                });
              } else {
                rows.push({
                  id: stableRowId,
                  title: cellTexts[0] || '',
                  ...(richHtml ? { titleHtml: richHtml } : {}),
                  ...productLink,
                  ...rowSettings,
                  subtotal: 0,
                });
              }
            } else {
              const amountInput = cellTexts[1]?.trim() ?? '';
              const usesText = amountInput !== '' && !isCurrencyAmount(amountInput);
              rows.push({
                id: stableRowId,
                title: cellTexts[0] || '',
                ...(richHtml ? { titleHtml: richHtml } : {}),
                ...productLink,
                ...rowSettings,
                subtotal: usesText ? 0 : parseEuroAmount(amountInput),
                ...(usesText ? { text: amountInput, useText: true } : {}),
              });
            }
          }

          tables.push({
            id: currentTableId || `table-${Date.now()}-${tables.length}`,
            title: currentPhaseTitle || 'Investeringsoverzicht',
            ...(currentPhaseSubtitle ? { subtitle: currentPhaseSubtitle } : {}),
            pricingType: tablePricingType,
            rows,
            columnWidths: readInvestmentColumnWidths(child, tablePricingType),
            ...(tablePricingType === 'hourly' && !currentShowHourColumns
              ? { showHourColumns: false }
              : {}),
            ...(currentShowRowPrices ? {} : { showRowPrices: false }),
          });
        } else if (child.type === 'investmentTotalNode') {
          const tAttrs = child.attrs as Record<string, unknown>;
          const tid = (tAttrs.tableId as string) || '';
          const pt = (tAttrs.pricingType as InvestmentTable['pricingType']) || 'fixed';
          const ft = Number(tAttrs.fixedTotal);
          const fixedTotalText =
            typeof tAttrs.fixedTotalText === 'string' && tAttrs.fixedTotalText.trim()
              ? tAttrs.fixedTotalText.trim()
              : undefined;
          const target =
            (tid && tables.find((t) => t.id === tid)) ?? (tables.length > 0 ? tables[tables.length - 1] : undefined);
          if (target) {
            target.showTotalRow = tAttrs.showTotalRow !== false;
            if (pt === 'fixed' && Number.isFinite(ft)) {
              target.fixedTotal = ft;
              target.fixedTotalText = fixedTotalText;
            }
          }
        }
      }

      const groupCurrency =
        (attrs.currency as string) ||
        prevInvContent?.currency ||
        'EUR';

      const invLang = normalizeInvestmentLabelLanguage(attrs.labelLanguage);
      const L = getInvestmentTableLabels(invLang);

      const rcAttr = attrs.rateCard as InvestmentRateCard | null | undefined;
      const rateCard: InvestmentRateCard | undefined =
        rcAttr === undefined
          ? prevInvContent?.rateCard
          : rcAttr === null
            ? undefined
            : rcAttr;

      blocks.push({
        id: blockId,
        quoteId,
        type: BlockType.INVESTMENT,
        order,
        content: {
          mainTitle,
          tables,
          currency: groupCurrency as InvestmentBlockContent['currency'],
          labelLanguage: invLang,
          tableLabels: {
            description: L.description,
            hourlyRate: L.hourlyRate,
            priceExTax: L.priceExTax,
            amountHours: L.amountHours,
            subtotal: L.subtotal,
            exTax: L.exTax,
            totalRow: L.totalRow,
          },
          theme:
            attrs.theme === 'ubx' || attrs.theme === 'ubo'
              ? attrs.theme
              : prevInvContent?.theme,
          clientLogoUrl: headerClientLogo ?? (attrs.clientLogoUrl as string | undefined),
          agencyLogoUrl: headerAgencyLogo,
          ...(rateCard ? { rateCard } : {}),
        } as InvestmentBlockContent,
        styles: (attrs.styles as Block['styles']) ?? {},
        config: (attrs.config as Block['config']) ?? {},
        createdAt: createdAtFor(blockId),
        updatedAt: now,
      });
      order += 1;
    } else {
      const blockType = NODE_TO_BLOCK_TYPE[nodeType];
      if (!blockType || !node.attrs) continue;

      const attrs = node.attrs as Record<string, unknown>;
      const blockId = (attrs.id as string) ?? `block-${Date.now()}-${order}`;
      blocks.push({
        id: blockId,
        quoteId,
        type: blockType,
        order,
        content: (attrs.content as Block['content']) ?? {},
        styles: (attrs.styles as Block['styles']) ?? {},
        config: (attrs.config as Block['config']) ?? {},
        createdAt: createdAtFor(blockId),
        updatedAt: now,
      });
      order += 1;
    }
  }

  return blocks;
}

export { BLOCK_TYPE_TO_NODE, NODE_TO_BLOCK_TYPE };
